import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Form, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import { QRCodeSVG } from "qrcode.react";
import { whoami } from "../services/APIClient";
import { isTeacher } from "../utils/auth";

import "../components/CustomQRGenerator.css";

function CustomQRGenerator() {
  const navigate = useNavigate();
  const [teacherid, setTeacherid] = useState(101);
  const [name, setName] = useState("John Doe");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  const [inputValue, setInputValue] = useState("");
  const [qrValue, setQrValue] = useState("");
  const qrRef = useRef(null);
  const printFrameRef = useRef(null);

  const teacherMode = isTeacher();

  // Redirect teachers away — alpaca staff only
  useEffect(() => {
    if (teacherMode) {
      navigate("/masterdashboard");
    }
  }, [teacherMode, navigate]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await whoami();
        const stakeholder = response.data.stakeholder;
        setTeacherid(stakeholder.id);
        setName(stakeholder.name);
      } catch (error) {
        console.error("Error in whoami:", error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };
    window.addEventListener("resize", handleOrientationChange);
    return () => window.removeEventListener("resize", handleOrientationChange);
  }, []);

  const handleGenerate = () => {
    if (inputValue.trim()) {
      setQrValue(inputValue.trim());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleDownloadPDF = async () => {
    if (!qrRef.current) return;

    const svgElement = qrRef.current.querySelector("svg");
    if (!svgElement) return;

    // Read the actual rendered size of the SVG on screen
    const svgWidth = svgElement.getAttribute("width") || svgElement.getBoundingClientRect().width;
    const svgHeight = svgElement.getAttribute("height") || svgElement.getBoundingClientRect().height;

    // Clone SVG and inline the Alpaca logo as base64 so it renders in the print iframe
    const svgClone = svgElement.cloneNode(true);
    const imageEl = svgClone.querySelector("image");
    if (imageEl) {
      try {
        const logoUrl = imageEl.getAttribute("href") || imageEl.getAttribute("xlink:href");
        if (logoUrl) {
          const resp = await fetch(logoUrl);
          const blob = await resp.blob();
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          imageEl.setAttribute("href", dataUrl);
          if (imageEl.hasAttribute("xlink:href")) {
            imageEl.setAttribute("xlink:href", dataUrl);
          }
        }
      } catch (err) {
        console.error("Could not inline logo for PDF:", err);
      }
    }

    const svgMarkup = new XMLSerializer().serializeToString(svgClone);
    const escapedValue = qrValue.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    // Use a hidden iframe instead of opening a new tab
    if (printFrameRef.current) {
      document.body.removeChild(printFrameRef.current);
    }
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.top = "-10000px";
    iframe.style.left = "-10000px";
    iframe.style.width = "0";
    iframe.style.height = "0";
    document.body.appendChild(iframe);
    printFrameRef.current = iframe;

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code</title>
        <style>
          @page { size: A4; margin: 20mm; }
          body {
            margin: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            font-family: Arial, sans-serif;
          }
          .qr-content { text-align: center; }
          .qr-content svg { width: ${svgWidth}px; height: ${svgHeight}px; }
          .qr-label {
            margin-top: 16px;
            font-size: 14px;
            color: #555;
            word-break: break-all;
            max-width: ${svgWidth}px;
          }
        </style>
      </head>
      <body>
        <div class="qr-content">
          ${svgMarkup}
          <div class="qr-label">${escapedValue}</div>
        </div>
      </body>
      </html>
    `);
    doc.close();

    // Wait for content to render, then trigger print
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };
  };

  return (
    <div>
      <Container fluid className="custom-qr-container">
        <HeadingBanner
          name={"QR Code Generator"}
          isLoggedIn={true}
          cloud={true}
          teacherid={teacherid}
        />

        <Row className="custom-qr-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="custom-qr-content">
            <div className="custom-qr-header">
              <h2 className="custom-qr-page-title">Custom QR Code Generator</h2>
              <p className="custom-qr-page-subtitle">
                Enter any URL or text to generate a downloadable QR code
              </p>
            </div>

            <Card className="custom-qr-card">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label className="custom-qr-label">
                    URL or Text
                  </Form.Label>
                  <div className="custom-qr-input-row">
                    <Form.Control
                      type="text"
                      placeholder="e.g. https://example.com"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="custom-qr-input"
                    />
                    <Button
                      variant="primary"
                      onClick={handleGenerate}
                      disabled={!inputValue.trim()}
                      className="custom-qr-generate-btn"
                    >
                      Generate
                    </Button>
                  </div>
                </Form.Group>

                {qrValue && (
                  <div className="custom-qr-result">
                    <div className="custom-qr-preview" ref={qrRef}>
                      <QRCodeSVG
                        value={qrValue}
                        size={400}
                        level={"H"}
                        includeMargin={true}
                        imageSettings={{
                          src: "/assets/imgs/Alpaca-logo-white-outline.png",
                          height: 100,
                          width: 80,
                        }}
                      />
                    </div>
                    <p className="custom-qr-value-display">{qrValue}</p>
                    <Button
                      variant="success"
                      onClick={handleDownloadPDF}
                      className="custom-qr-download-btn"
                    >
                      Download as PDF
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}
    </div>
  );
}

export default CustomQRGenerator;
